import formIconOptions from "@jumbo/constants/formIconOptions";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { Add, DeleteOutline, EditOutlined, Refresh } from "@material-ui/icons";
import { Alert, Autocomplete, Pagination } from "@material-ui/lab";
import PageContainer from "@jumbo/components/PageComponents/layouts/PageContainer";
import FileUploader from "@jumbo/Dashboards/Media/FileUploader";
import { baseUrl } from "@jumbo/constants/baseUrl";
import axios from "services/auth/jwt/config";
import { getMyAccess } from "services/adminAccess";
import reportCardService from "services/reportCard";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import TimePicker from "react-multi-date-picker/plugins/time_picker";

const emptyCard = () => ({
  title: "",
  subtitle: "",
  description: "",
  icon: "",
  poster: "",
  type: "Exam",
  hasForm: false,
  form: "",
  allowObjection: false,
  allowObjectionTill: null
});
const errorMessage = error =>
  (error.response && error.response.data && error.response.data.msgFa) ||
  "انجام عملیات ممکن نشد. لطفاً دوباره تلاش کنید.";

export default function ReportCards() {
  const [access, setAccess] = useState(null);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState(emptyCard);
  const [editorError, setEditorError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [mediaField, setMediaField] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formSearch, setFormSearch] = useState("");
  const [formOptions, setFormOptions] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState("");

  useEffect(() => {
    if (!editor || !draft.hasForm) return;
    let active = true;
    setFormsLoading(true);
    setFormsError("");
    setFormOptions([]);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get("/form/getall", {
          params: { page: 1, limit: 10, searchString: formSearch }
        });
        if (active) {
          setFormOptions(data.docs || []);
          setSelectedForm(current => current
            ? (data.docs || []).find(form => form._id === current._id) || current
            : null);
        }
      } catch (err) {
        if (active) setFormsError(errorMessage(err));
      } finally {
        if (active) setFormsLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [editor, draft.hasForm, formSearch]);

  useEffect(() => {
    if (!editor || !draft.hasForm || !draft.form) return;
    let active = true;
    axios.get(`/form/get/${draft.form}`).then(({ data }) => {
      if (active && data) setSelectedForm(data);
    }).catch(() => {
      // Preserve the saved selection if its details cannot be loaded.
    });
    return () => { active = false; };
  }, [editor, draft.hasForm, draft.form]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const permission = await getMyAccess("ReportCard");
        if (!active) return;
        setAccess(permission);
        if (!permission) return;
        const { data } = await reportCardService.getAll(page);
        if (!active) return;
        if (page > Math.max(1, data.totalPages)) {
          setPage(Math.max(1, data.totalPages));
          return;
        }
        setRecords(data.docs);
        setTotalPages(Math.max(1, data.totalPages));
        setTotalDocs(data.totalDocs);
      } catch (err) {
        if (active) setError(errorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [page, refresh]);

  const change = (field, value) =>
    setDraft(current => ({ ...current, [field]: value }));
  const closeEditor = () => {
    if (!busy) setEditor(null);
  };
  const openCreate = () => {
    setDraft(emptyCard());
    setSelectedForm(null);
    setFormSearch("");
    setEditorError("");
    setSuccess("");
    setEditor({ id: null });
  };
  const openEdit = async id => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await reportCardService.getById(id);
      setSelectedForm(data.form
        ? typeof data.form === "object" ? data.form : { _id: data.form }
        : null);
      setFormSearch("");
      setDraft({
        ...emptyCard(),
        ...data,
        form: (data.form && (data.form._id || data.form)) || ""
      });
      setEditorError("");
      setEditor({ id });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    if (!draft.title.trim()) {
      setEditorError("عنوان کارنامه را وارد کنید.");
      return;
    }
    if (draft.hasForm && !/^[a-f\d]{24}$/i.test(draft.form.trim())) {
      setEditorError("فرم مورد نظر را از فهرست انتخاب کنید.");
      return;
    }
    setBusy(true);
    setEditorError("");
    const payload = {
      title: draft.title.trim(),
      subtitle: draft.subtitle,
      description: draft.description,
      icon: formIconOptions.some(option => option.value === draft.icon) ? draft.icon : "",
      poster: draft.poster.trim(),
      type: draft.type,
      hasForm: draft.hasForm,
      form: draft.hasForm ? draft.form.trim() : null,
      allowObjection: draft.allowObjection,
      allowObjectionTill: draft.allowObjectionTill || null
    };
    try {
      if (editor.id) await reportCardService.update(editor.id, payload);
      else await reportCardService.create(payload);
      setEditor(null);
      setSuccess(
        editor.id
          ? "کارنامه با موفقیت ویرایش شد."
          : "کارنامه با موفقیت ایجاد شد."
      );
      if (!editor.id) setPage(1);
      setRefresh(value => value + 1);
    } catch (err) {
      setEditorError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setDeleteError("");
    try {
      await reportCardService.remove(deleteTarget._id);
      setDeleteTarget(null);
      setSuccess("کارنامه با موفقیت حذف شد.");
      if (records.length === 1 && page > 1) setPage(page - 1);
      else setRefresh(value => value + 1);
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer
      heading="مدیر کارنامه"
      breadcrumbs={[
        { label: "خانه", link: "/" },
        { label: "مدیر کارنامه", isActive: true }
      ]}
    >
      <Box dir="rtl">
        {success && (
          <Box mb={2}>
            <Alert severity="success" onClose={() => setSuccess("")}>
              {success}
            </Alert>
          </Box>
        )}
        {error && (
          <Box mb={2}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  onClick={() => setRefresh(value => value + 1)}
                >
                  تلاش مجدد
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        )}
        {access === false && (
          <Alert severity="warning">
            شما به بخش مدیر کارنامه دسترسی ندارید.
          </Alert>
        )}
        {access && (
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Typography color="textSecondary">
              ایجاد و مدیریت کارنامه‌ها
            </Typography>
            <Box display="flex" my={1}>
              <Button
                disabled={busy || loading}
                startIcon={<Refresh />}
                onClick={() => setRefresh(value => value + 1)}
              >
                بازخوانی
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                disabled={busy}
                onClick={openCreate}
              >
                ایجاد کارنامه
              </Button>
            </Box>
          </Box>
        )}
        {loading ? (
          <Box p={5} textAlign="center">
            <CircularProgress aria-label="در حال دریافت کارنامه‌ها" />
          </Box>
        ) : (
          access &&
          !error && (
            <Paper>
              <TableContainer>
                <Table aria-label="فهرست کارنامه‌ها">
                  <TableHead>
                    <TableRow>
                      <TableCell>عنوان</TableCell>
                      <TableCell>زیرعنوان</TableCell>
                      <TableCell>نوع</TableCell>
                      <TableCell>فرم مرتبط</TableCell>
                      <TableCell>عملیات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box py={5}>
                            <Typography>
                              هنوز کارنامه‌ای ایجاد نشده است.
                            </Typography>
                            <Button color="primary" onClick={openCreate}>
                              ایجاد اولین کارنامه
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map(record => (
                        <TableRow key={record._id} hover>
                          <TableCell>{record.title}</TableCell>
                          <TableCell>{record.subtitle || "—"}</TableCell>
                          <TableCell>
                            {record.type === "Exam" ? "آزمون" : record.type}
                          </TableCell>
                          <TableCell>
                            {record.hasForm ? "دارد" : "ندارد"}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexWrap="wrap">
                              <Button component={Link} to={`/reportcards/${record._id}/objections`} color="primary"
                                aria-label={`اعتراض‌های ${record.title}`}>
                                اعتراض‌ها
                              </Button>
                              <Button
                                component={Link}
                                to={`/reportcards/${record._id}/results`}
                                color="primary"
                                aria-label={`نتایج ${record.title}`}
                              >
                                نتایج
                              </Button>
                              <Button
                                color="primary"
                                startIcon={<EditOutlined />}
                                disabled={busy}
                                onClick={() => openEdit(record._id)}
                                aria-label={`ویرایش ${record.title}`}
                              >
                                ویرایش
                              </Button>
                              <Button
                                color="secondary"
                                startIcon={<DeleteOutline />}
                                disabled={busy}
                                onClick={() => {
                                  setDeleteError("");
                                  setSuccess("");
                                  setDeleteTarget(record);
                                }}
                                aria-label={`حذف ${record.title}`}
                              >
                                حذف
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                p={2}
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="textSecondary">
                  {totalDocs} کارنامه
                </Typography>
                <Pagination
                  getItemAriaLabel={(type, number) =>
                    type === "page"
                      ? `صفحه ${number}`
                      : type === "next"
                      ? "صفحه بعد"
                      : "صفحه قبل"
                  }
                  count={totalPages}
                  page={page}
                  disabled={busy}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </Paper>
          )
        )}

        <Dialog
          open={Boolean(editor)}
          onClose={closeEditor}
          fullWidth
          maxWidth="md"
          aria-labelledby="reportcard-editor-title"
        >
          <form onSubmit={save} dir="rtl">
            <DialogTitle id="reportcard-editor-title">
              {editor && editor.id ? "ویرایش کارنامه" : "ایجاد کارنامه"}
            </DialogTitle>
            <DialogContent dividers>
              {editorError && (
                <Box mb={2}>
                  <Alert severity="error">{editorError}</Alert>
                </Box>
              )}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    id="reportcard-title"
                    label="عنوان کارنامه"
                    required
                    fullWidth
                    autoFocus
                    variant="outlined"
                    value={draft.title}
                    disabled={busy}
                    onChange={event => change("title", event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    id="reportcard-type"
                    label="نوع کارنامه"
                    fullWidth
                    variant="outlined"
                    value={draft.type}
                    disabled={busy}
                    onChange={event => change("type", event.target.value)}
                  >
                    <MenuItem value="Exam">آزمون</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="reportcard-subtitle"
                    label="زیرعنوان"
                    fullWidth
                    variant="outlined"
                    value={draft.subtitle}
                    disabled={busy}
                    onChange={event => change("subtitle", event.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="reportcard-description"
                    label="توضیحات"
                    fullWidth
                    multiline
                    minRows={4}
                    variant="outlined"
                    value={draft.description}
                    disabled={busy}
                    onChange={event =>
                      change("description", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="reportcard-icon"
                    label="آیکون کارنامه"
                    select
                    fullWidth
                    variant="outlined"
                    value={
                      formIconOptions.some(
                        option => option.value === draft.icon
                      )
                        ? draft.icon
                        : ""
                    }
                    disabled={busy}
                    onChange={event => change("icon", event.target.value)}
                  >
                    <MenuItem value="">بدون آیکون</MenuItem>
                    {formIconOptions.map(({ value, Icon }) => (
                      <MenuItem key={value} value={value} aria-label={value}>
                        <Icon
                          fontSize="small"
                          style={{ marginBottom: "-5px" }}
                        />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {[{ field: "poster", label: "پوستر" }].map(
                  ({ field, label }) => (
                    <Grid key={field} item xs={12} sm={6}>
                      <TextField
                        id={`reportcard-${field}`}
                        label={`نشانی ${label}`}
                        fullWidth
                        variant="outlined"
                        value={draft[field]}
                        disabled={busy}
                        onChange={event => change(field, event.target.value)}
                        inputProps={{ dir: "ltr" }}
                      />
                      <Button
                        color="primary"
                        disabled={busy}
                        onClick={() => setMediaField(field)}
                      >
                        انتخاب {label} از رسانه‌ها
                      </Button>
                    </Grid>
                  )
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    label="اتصال به فرم"
                    control={
                      <Switch
                        color="primary"
                        checked={draft.hasForm}
                        disabled={busy}
                        onChange={event =>
                          change("hasForm", event.target.checked)
                        }
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    label="اجازه ثبت اعتراض"
                    control={<Switch
                      color="primary"
                      checked={Boolean(draft.allowObjection)}
                      disabled={busy}
                      onChange={event => change("allowObjection", event.target.checked)}
                    />}
                  />
                </Grid>
                {draft.allowObjection && (
                  <Grid item xs={12}>
                    <Typography component="label" htmlFor="reportcard-objection-deadline" display="block" gutterBottom>
                      مهلت ثبت اعتراض (اختیاری)
                    </Typography>
                    <DatePicker
                      id="reportcard-objection-deadline"
                      calendar={persian}
                      locale={persian_fa}
                      format="YYYY/MM/DD HH:mm:ss"
                      value={draft.allowObjectionTill ? new Date(draft.allowObjectionTill) : ""}
                      onChange={date => change("allowObjectionTill", date && date.isValid ? date.toDate().toISOString() : null)}
                      disabled={busy}
                      editable={false}
                      calendarPosition="top-right"
                      plugins={[<TimePicker key="time" position="bottom" />]}
                    />
                    <Button disabled={busy || !draft.allowObjectionTill} onClick={() => change("allowObjectionTill", null)}>
                      پاک کردن مهلت
                    </Button>
                  </Grid>
                )}
                {draft.hasForm && (
                  <Grid item xs={12}>
                    <Autocomplete
                      id="reportcard-form"
                      value={selectedForm}
                      options={selectedForm && !formOptions.some(form => form._id === selectedForm._id)
                        ? [selectedForm, ...formOptions] : formOptions}
                      getOptionLabel={form => form.title || form._id || ""}
                      getOptionSelected={(option, value) => option._id === value._id}
                      filterOptions={options => options}
                      disabled={busy}
                      loading={formsLoading}
                      loadingText="در حال دریافت فرم‌ها…"
                      noOptionsText={formsError || "فرمی یافت نشد."}
                      clearText="پاک کردن"
                      openText="نمایش فرم‌ها"
                      closeText="بستن"
                      onInputChange={(_, value, reason) => {
                        if (reason === "input" || reason === "clear") setFormSearch(value);
                      }}
                      onChange={(_, form) => {
                        setSelectedForm(form);
                        change("form", form ? form._id : "");
                      }}
                      renderInput={params => <TextField {...params} label="فرم مرتبط" required fullWidth variant="outlined"
                        error={Boolean(formsError)}
                        helperText={formsError || "عنوان فرم را جستجو و فرم مورد نظر را انتخاب کنید."}
                        InputProps={{ ...params.InputProps, endAdornment: <>
                          {formsLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </> }} />}
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={busy} onClick={closeEditor}>
                انصراف
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={busy}
              >
                {busy ? "در حال ذخیره…" : "ذخیره کارنامه"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {mediaField && (
          <FileUploader
            selectMode
            open
            handleClose={() => setMediaField(null)}
            gallerySelctor={true}
            gallerySlected={null}
            handleSelectGallery={selected => {
              change(mediaField, `${baseUrl}file/get/${selected.name}`);
              setMediaField(null);
            }}
          />
        )}
        <Dialog
          open={Boolean(deleteTarget)}
          onClose={() => {
            if (!busy) setDeleteTarget(null);
          }}
          aria-labelledby="reportcard-delete-title"
          dir="rtl"
        >
          <DialogTitle id="reportcard-delete-title">حذف کارنامه</DialogTitle>
          <DialogContent>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <DialogContentText>
              آیا از حذف کارنامه «{deleteTarget && deleteTarget.title}» مطمئن
              هستید؟ این عملیات قابل بازگشت نیست.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              disabled={busy}
              onClick={() => setDeleteTarget(null)}
            >
              انصراف
            </Button>
            <Button color="secondary" disabled={busy} onClick={remove}>
              {busy ? "در حال حذف…" : "حذف کارنامه"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageContainer>
  );
}
